import React, { useEffect, useMemo, useRef } from "react";

import {
  StyledActionWrapper,
  StyledCommandPaletteHeader,
  StyledInput,
  StyledParameter,
  StyledSearchIcon,
  StyledSpinnerLoader,
} from "./CommandPalette.styles";
import { useSelectedContext } from "./context";
import { useModeContext } from "./context/ModeContext";
import { useSearchContext } from "./context/SearchContext";
import { IconButton } from "../IconButton";
import { CloseIcon, HashtagIcon, SearchLargeIcon } from "../Icons";
import { Tooltip } from "../Tooltip";

export interface CommandPaletteHeaderProps {
  loading?: boolean;
  commandsLength: number;
  itemsLength: number;
}

export const CommandPaletteHeader = ({
  loading = false,
  commandsLength,
  itemsLength,
}: CommandPaletteHeaderProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { mode, setMode, idModeEnabled } = useModeContext();
  const { setSelected, selected } = useSelectedContext();
  const { search, onChangeSearch } = useSearchContext();

  const focusInput = () => {
    if (inputRef && inputRef.current) {
      inputRef.current.focus();
    }
  };

  const clearInput = () => {
    if (inputRef && inputRef.current) {
      inputRef.current.value = "";
      onChangeSearch("");
    }
  };

  useEffect(() => {
    if (search.length > 0) {
      if (itemsLength > commandsLength) {
        setSelected(commandsLength);
      } else {
        setSelected(0);
      }
    }
  }, [search, commandsLength, itemsLength, setSelected]);

  useEffect(() => {
    focusInput();
  }, [mode]);

  const handleInputPlaceholder = useMemo(() => {
    if (typeof mode === "object") {
      return "Search";
    }

    if (mode === "id") {
      return "1";
    }

    return "Search or jump to";
  }, [mode]);

  const handleKeyDown = (e) => {
    if (
      e.key === "Backspace" &&
      mode !== "default" &&
      (e.target as HTMLInputElement).value === ""
    ) {
      setMode("default");
      clearInput();
    }

    if (e.key === "Enter") {
      const selectedItem = document.querySelectorAll(".cp-item")[
        selected
      ] as HTMLElement;

      if (itemsLength > 0) {
        selectedItem.click();
      }
    }
  };

  return (
    <StyledCommandPaletteHeader className="cp-header">
      <StyledSearchIcon>
        <SearchLargeIcon />
      </StyledSearchIcon>

      {mode === "id" ? <StyledParameter>ID#:</StyledParameter> : null}
      {typeof mode === "object" ? (
        <StyledParameter>
          {mode.name.length > 10 ? mode.name.slice(0, 10) + "..." : mode.name}/
        </StyledParameter>
      ) : null}

      <StyledInput
        ref={inputRef}
        autoFocus
        placeholder={handleInputPlaceholder}
        type="search"
        aria-autocomplete="list"
        autoCapitalize="none"
        autoComplete="off"
        autoCorrect="off"
        role="combobox"
        spellCheck={false}
        value={search}
        onKeyDown={handleKeyDown}
        onChange={(e) => {
          const inputText = e.target.value;

          if (
            inputText.startsWith("#") &&
            idModeEnabled &&
            search.length === 0
          ) {
            setMode("id");
            onChangeSearch(inputText.slice(1));
            return;
          }

          onChangeSearch(inputText);
        }}
      />

      <StyledActionWrapper>
        {loading ? (
          <StyledSpinnerLoader
            aria-busy={true}
            aria-label="Loading..."
            aria-valuemax={100}
            aria-valuemin={0}
            role="progressbar"
          />
        ) : null}

        {mode !== "id" &&
        typeof mode !== "object" &&
        idModeEnabled &&
        search.length === 0 ? (
          <Tooltip title="Search by ID" placement="top">
            <IconButton
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  setMode("id");
                  clearInput();
                }
              }}
              onClick={() => {
                setMode("id");
                clearInput();
              }}
              variant="text gray"
            >
              <HashtagIcon />
            </IconButton>
          </Tooltip>
        ) : null}

        {search.length > 0 || mode === "id" || typeof mode === "object" ? (
          <Tooltip title="Clear" placement="top">
            <IconButton
              variant="text gray"
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  setMode("default");
                  clearInput();
                  focusInput();
                }
              }}
              onClick={() => {
                setMode("default");
                clearInput();
                focusInput();
              }}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        ) : null}
      </StyledActionWrapper>
    </StyledCommandPaletteHeader>
  );
};

CommandPaletteHeader.displayName = "CommandPaletteItem";
